import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { Card, Spin, Select, Row, Col } from "antd";

import {
    PieChart,
    Pie,
    Tooltip,
    Legend,
    Cell,
    ResponsiveContainer,
} from "recharts";

const { Option } = Select;

/* ================= VALID STATUSES ================= */
const VALID_STATUSES = [
    "PRESENT",
    "ABSENT",
    "LEAVE",
    "HALF_DAY_LEAVE",
    "HALFDAYLEAVE",
    "PERMISSION",
    "HOLIDAY",
    "WEEK_OFF", // combined
    "COMPOFF",
    "LOP",
    "TECHNICAL_ISSUE"
];

export default function IndividualAttendance() {
    const { authData, backUrl } = useContext(AuthContext);

    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const employeeId = authData?.employee?.id || null;

    const isMobile = window.innerWidth < 480;
    const isTab = window.innerWidth < 768;
    /* ================= MONTH & YEAR OPTIONS ================= */
    const monthOptions = [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" },
    ];

    const yearOptions = Array.from({ length: 6 }, (_, i) => selectedYear - 3 + i);

    /* ================= STATUS → COLOR MAP ================= */
    const statusColors = {
        PRESENT: "green",
        ABSENT: "red",
        LEAVE: "red",
        HALF_DAY_LEAVE: "#ff9999",
        HALFDAYLEAVE: "#ff9999",
        PERMISSION: "lightgreen",
        HOLIDAY: "orange",
        WEEK_OFF: "yellow",
        COMPOFF: "blue",
        LOP: "red",
        TECHNICAL_ISSUE: 'gray'
    };

    /* ================= FETCH DATA ================= */
    const fetchData = async () => {
        if (!employeeId) return;

        try {
            setLoading(true);

            const res = await axios.get(
                `${backUrl}/api/hr/calendar/?employee_id=${employeeId}&month=${selectedMonth}&year=${selectedYear}`,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );

            const data = res.data.results || res.data || [];

            const formatted = data.map((item) => ({
                ...item,
                finalStatus:
                    item.AttendanceStatus ||
                    item.Status ||
                    item.status ||
                    null,
            }));

            setAttendanceData(formatted);
        } catch (error) {
            console.error("Attendance Fetch Error:", error);
            setAttendanceData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedMonth, selectedYear, employeeId]);

    /* ================= PIE DATA (MERGED Off Day Working) ================= */
    const statusCounts = attendanceData.reduce((acc, row) => {
        let status = row.finalStatus?.toUpperCase();

        /* ===== Merge Weekoff types ===== */
        if (["SUNDAY", "WEEKOFF", "OFF DAY WORKING"].includes(status)) {
            status = "WEEK_OFF";
        }

        /* ===== Merge Leave / Absent / LOP ===== */
        if (["LEAVE", "ABSENT", "LOP"].includes(status)) {
            status = "LEAVE";
        }

        /* ===== Merge Half Day Leave ===== */
        if (["HALF_DAY_LEAVE", "HALFDAYLEAVE"].includes(status)) {
            status = "HALF_DAY_LEAVE";
        }

        if (!VALID_STATUSES.includes(status)) return acc;

        acc[status] = (acc[status] || 0) + 1;
        return acc;
    }, {});


    const pieData = Object.entries(statusCounts).map(([name, value]) => ({
        name,
        value,
    }));

    /* ================= UI ================= */
    return (
        <div style={{ paddingTop: "5px" }}>
            <Card style={{ width: "100%" }}>
                {/* Filters */}
                <div style={{ marginBottom: "20px" }}>
                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                                Month
                            </label>
                            <Select
                                value={selectedMonth}
                                onChange={setSelectedMonth}
                                style={{ width: "100%" }}
                            >
                                {monthOptions.map((month) => (
                                    <Option key={month.value} value={month.value}>
                                        {month.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>

                        <Col span={12}>
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                                Year
                            </label>
                            <Select
                                value={selectedYear}
                                onChange={setSelectedYear}
                                style={{ width: "100%" }}
                            >
                                {yearOptions.map((year) => (
                                    <Option key={year} value={year}>
                                        {year}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                    </Row>
                </div>

                {/* Chart */}
                {loading ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                        <Spin size="large" />
                    </div>
                ) : pieData.length > 0 ? (
                    <div style={{ width: "100%", height: 350 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    label={({ value, x, y }) => (
                                        <text
                                            x={x - 5}
                                            y={y - 10}
                                            fill="#000"
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            fontSize={18}
                                            fontWeight="bold"
                                        >
                                            {value}
                                        </text>
                                    )}
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={statusColors[entry.name]}
                                        />
                                    ))}
                                </Pie>
                                <br />

                                <Tooltip
                                    formatter={(value, name) => [
                                        value,
                                        name === "WEEK_OFF"
                                            ? "Week Off"
                                            : name === "HALF_DAY_LEAVE"
                                                ? "Half day Leave"
                                                : name,
                                    ]}
                                />

                                <Legend
                                    formatter={(value) =>
                                        value === "WEEK_OFF"
                                            ? "Week Off"
                                            : value === "HALF_DAY_LEAVE"
                                                ? "Half day Leave"
                                                : value
                                    }
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                        No attendance data available for the selected period
                    </div>
                )}
            </Card>
        </div>
    );
}
